import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  Search, Plus, User, Calendar, Upload, List, LayoutGrid,
  CheckCircle2, Loader2, X, ChevronRight, ChevronLeft, Users, Trash2, History, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Checkbox } from '../../components/ui/checkbox';
import CCDAImportModal from '../../components/lumina/CCDAImportModal';
import ImportPatientsModal from '../../components/lumina/ImportPatientsModal';
import AddPatientModal from '../../components/lumina/patients/AddPatientModal';
import UploadPatientsModal from '../../components/lumina/patients/UploadPatientsModal';
import ScanIDModal from '../../components/lumina/patients/ScanIDModal';
import RollbackHistoryModal from '../../components/lumina/patients/RollbackHistoryModal';
import { useBulkImportFlow } from '../../hooks/lumina/useBulkImportFlow';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Sub-components ──

const EmptyState = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="patients-empty-state">
    <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <Users className="h-7 w-7 text-slate-400" />
    </div>
    <h3 className="text-sm font-semibold text-slate-700 mb-1">No patients found</h3>
    <p className="text-xs text-slate-500 mb-4 max-w-[280px]">Add your first patient manually, upload a file, or scan an ID card to get started.</p>
    <Button className="bg-black hover:bg-gray-800 gap-2 text-xs" onClick={onAdd} data-testid="empty-add-patient-btn">
      <Plus className="h-3.5 w-3.5" /> Add Patient
    </Button>
  </div>
);


// ── Main Component ──

const LuminaPatients = () => {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(null); // null = unknown
  const [hasMore, setHasMore] = useState(false);

  // Modal states
  const [showAddPatient, setShowAddPatient] = useState(false);
  const { showUpload, setShowUpload, preloadedUploadFile, setPreloadedUploadFile, minimizedJobId, setMinimizedJobId } = useBulkImportFlow();
  const [showScan, setShowScan] = useState(false);
  const [showCCDA, setShowCCDA] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [scanPrefill, setScanPrefill] = useState(null);
  const [preloadedScanFile, setPreloadedScanFile] = useState(null);
  const [preloadedCCDAFiles, setPreloadedCCDAFiles] = useState(null);

  // Delete / selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name } for single delete confirm
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ done: 0, total: 0 });

  // Rollback history state
  const [showRollbackHistory, setShowRollbackHistory] = useState(false);

  // Edit patient state
  const [editTarget, setEditTarget] = useState(null); // full patient object
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // ── Fetch patients (paginated) ── API-only; no mock fallback in real mode.
  // All call sites pass `(search, pg, sz)` explicitly, so the callback has no
  // stale-closure exposure to `page` / `pageSize` and stays referentially
  // stable across pagination clicks.
  const [fetchError, setFetchError] = useState(null);
  const fetchPatients = useCallback(async (search, pg, sz) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', String(pg));
      params.set('limit', String(sz));
      const qs = params.toString();
      const res = await authFetch(`${API}/api/patients?${qs}`);
      if (res.ok) {
        const data = await res.json();
        // Handle normalized envelope from backend
        const list = data.patients || data.items || data.data || data.results || (Array.isArray(data) ? data : []);
        setPatients(list);
        setTotalCount(typeof data.total === 'number' ? data.total : null);
        setHasMore(typeof data.has_more === 'boolean' ? data.has_more : list.length >= sz);
        setPage(pg);
      } else if (res.status === 401) {
        // Session invalid after refresh attempt — AuthContext.authFetch
        // already called signOut on refresh-failure, which clears the token
        // and flips the AuthContext into the signed-out state; the router
        // will redirect to the login screen on the next render. We just
        // clear the list here so stale state doesn't linger.
        setPatients([]);
        setTotalCount(0);
        setHasMore(false);
        setFetchError({ status: 401, message: 'Your session has expired. Please sign in again.' });
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || 'Failed to load patients';
        toast.error(msg);
        setPatients([]);
        setTotalCount(0);
        setHasMore(false);
        setFetchError({ status: res.status, message: msg });
      }
    } catch (err) {
      // Network error in real mode — surface an error banner. NEVER fall
      // back to fabricated patient rows (clinical safety: showing fake
      // patient data is worse than showing no data).
      setPatients([]);
      setTotalCount(0);
      setHasMore(false);
      setFetchError({ status: 0, message: err?.message || 'Network error — unable to reach the server.' });
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { fetchPatients('', 1, pageSize); }, [pageSize, fetchPatients]);

  // Debounced search (resets to page 1)
  const searchTimer = useRef(null);
  const handleSearch = (val) => {
    setSearchTerm(val);
    setSelectedIds(new Set());
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPatients(val, 1, pageSize), 400);
  };

  const filteredPatients = patients;
  const patientCount = filteredPatients.length;
  const displayTotal = totalCount !== null ? totalCount : patientCount;

  // Pagination helpers
  const totalPages = totalCount !== null ? Math.ceil(totalCount / pageSize) : null;
  const goToPage = (pg) => { setSelectedIds(new Set()); fetchPatients(searchTerm, pg, pageSize); };
  const handlePageSizeChange = (sz) => { setSelectedIds(new Set()); setPageSize(sz); setPage(1); fetchPatients(searchTerm, 1, sz); };

  // ── Selection helpers ──
  const getPatientId = (p, i) => p.id || p._id || p.patient_id || `row-${i}`;
  const getPatientName = (p) => p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';

  const allVisibleIds = filteredPatients.map((p, i) => getPatientId(p, i));
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allVisibleIds));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Single delete ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const res = await authFetch(`${API}/api/patients/${deleteTarget.id}`, { method: 'DELETE' });
      if (res.ok || res.status === 204) {
        toast.success(`${deleteTarget.name} deleted`);
        setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
        fetchPatients(searchTerm, page, pageSize);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to delete patient (${res.status})`);
      }
    } catch {
      toast.error('Network error deleting patient');
    }
    setDeleteTarget(null);
  };

  // ── Bulk delete (max 3 concurrency) ──
  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setShowBulkDeleteConfirm(false);
    setBulkDeleting(true);
    setBulkDeleteProgress({ done: 0, total: ids.length });

    let deleted = 0, failed = 0;
    const failedIds = new Set();

    for (let i = 0; i < ids.length; i += 3) {
      const chunk = ids.slice(i, i + 3);
      const results = await Promise.allSettled(
        chunk.map(async (id) => {
          const res = await authFetch(`${API}/api/patients/${id}`, { method: 'DELETE' });
          return { id, ok: res.ok || res.status === 204 };
        })
      );
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value.ok) {
          deleted++;
        } else {
          failed++;
          const failId = r.status === 'fulfilled' ? r.value.id : chunk[results.indexOf(r)];
          if (failId) failedIds.add(failId);
        }
      }
      setBulkDeleteProgress({ done: Math.min(i + chunk.length, ids.length), total: ids.length });
    }

    setBulkDeleting(false);
    setSelectedIds(failedIds);
    const parts = [`${deleted} deleted`];
    if (failed > 0) parts.push(`${failed} failed`);
    toast[failed > 0 ? 'warning' : 'success'](`Bulk delete: ${parts.join(', ')}`);
    fetchPatients(searchTerm, page, pageSize);
  };

  // ── Edit patient ──
  const openEditPatient = (p, i) => {
    const pid = getPatientId(p, i);
    setEditForm({
      first_name: p.first_name || p.firstName || (p.name || '').split(' ')[0] || '',
      last_name: p.last_name || p.lastName || (p.name || '').split(' ').slice(1).join(' ') || '',
      date_of_birth: p.date_of_birth || p.dob || '',
      gender: p.gender || p.sex || '',
      phone: p.phone || '',
      email: p.email || '',
      payer_name: p.payer_name || p.insurance || '',
      member_id: p.member_id || '',
    });
    setEditTarget({ id: pid, name: getPatientName(p) });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => { if (v) payload[k] = v; });
      const res = await authFetch(`${API}/api/patients/${editTarget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(`Patient "${editTarget.name}" updated`);
        setEditTarget(null);
        fetchPatients(searchTerm, page, pageSize);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Update failed (${res.status})`);
      }
    } catch {
      toast.error('Network error updating patient');
    }
    setEditSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Patients</h1>
          <p className="text-slate-500 mt-1" data-testid="patients-count-label">
            {loading ? 'Loading...' : totalCount !== null
              ? `${totalCount} patient${totalCount !== 1 ? 's' : ''} total · showing ${patientCount}`
              : `${patientCount} patient${patientCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm"
              className={cn("rounded-none gap-1.5", viewMode === 'list' && "bg-black text-white")}
              onClick={() => setViewMode('list')} data-testid="patients-view-list">
              <List className="h-4 w-4" /> List
            </Button>
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm"
              className={cn("rounded-none gap-1.5", viewMode === 'grid' && "bg-black text-white")}
              onClick={() => setViewMode('grid')} data-testid="patients-view-grid">
              <LayoutGrid className="h-4 w-4" /> Cards
            </Button>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => setShowImportModal(true)} data-testid="import-patients-btn"
            style={{ height: 36, padding: '0 16px', borderRadius: 8, border: '0.5px solid #e0e0e0', background: '#fff', color: '#555', fontSize: 13 }}>
            <Upload className="h-3 w-3" /> Import patients
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowRollbackHistory(true)} data-testid="import-history-btn">
            <History className="h-4 w-4" /> Import History
          </Button>
          <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => setShowAddPatient(true)} data-testid="add-patient-btn">
            <Plus className="h-4 w-4" /> Add Patient
          </Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-slate-700" data-testid="selected-count">
            {selectedIds.size} selected
          </span>
          <div className="h-4 w-px bg-slate-300" />
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
            disabled={bulkDeleting}
            onClick={() => setShowBulkDeleteConfirm(true)}
            data-testid="bulk-delete-btn">
            <Trash2 className="h-3 w-3" />
            Delete Selected ({selectedIds.size})
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"
            onClick={() => setSelectedIds(new Set())}
            data-testid="clear-selection-btn">
            <X className="h-3 w-3" /> Clear Selection
          </Button>
          {bulkDeleting && (
            <span className="text-xs text-slate-500 ml-auto" data-testid="bulk-delete-progress">
              Deleting {bulkDeleteProgress.done}/{bulkDeleteProgress.total}...
            </span>
          )}
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by name, MRN, or DOB..." className="pl-10" value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)} data-testid="patients-search" />
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}

      {/* Error banner — replaces the prior silent mock-patient fallback.
          Clinical safety: never fabricate patient rows when the backend is
          unreachable or rejects our credentials. */}
      {!loading && fetchError && (
        <Card className="border-red-200 bg-red-50/40" data-testid="patients-error-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">
                {fetchError.status === 401 ? 'Session expired' : 'Unable to load patients'}
              </p>
              <p className="text-xs text-red-700 mt-0.5">{fetchError.message}</p>
            </div>
            {fetchError.status !== 401 && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => fetchPatients(searchTerm, page, pageSize)}
                data-testid="patients-retry-btn"
              >
                Retry
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !fetchError && patientCount === 0 && <EmptyState onAdd={() => setShowAddPatient(true)} />}

      {/* List View */}
      {!loading && patientCount > 0 && viewMode === 'list' && (
        <Card data-testid="patients-list-view">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pl-4">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox" />
                  </TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>MRN</TableHead>
                  <TableHead>DOB / Gender</TableHead>
                  <TableHead>Insurance</TableHead>
                  <TableHead>Last Visit</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatients.map((p, i) => {
                  const name = getPatientName(p);
                  const pid = getPatientId(p, i);
                  const isSelected = selectedIds.has(pid);
                  return (
                    <TableRow key={pid} className={cn("cursor-pointer hover:bg-slate-50", isSelected && "bg-slate-50")}
                      onClick={() => navigate(`/lumina/clinical-focus?patientId=${pid}`)}
                      data-testid={`patient-row-${pid}`}>
                      <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(pid)}
                          data-testid={`select-patient-${pid}`} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          <span className="font-medium text-sm">{name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{p.mrn || p.medical_record_number || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {p.date_of_birth || p.dob || '—'}
                        {(p.gender || p.sex) && <span className="text-muted-foreground"> · {p.gender || p.sex}</span>}
                      </TableCell>
                      <TableCell>{(p.insurance || p.payer_name) ? <Badge variant="outline" className="text-xs">{p.insurance || p.payer_name}</Badge> : '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.last_visit || p.lastVisit || '—'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                            onClick={() => openEditPatient(p, i)}
                            data-testid={`edit-patient-${pid}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                            onClick={() => setDeleteTarget({ id: pid, name })}
                            data-testid={`delete-patient-${pid}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Grid View */}
      {!loading && patientCount > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="patients-grid-view">
          {filteredPatients.map((p, i) => {
            const name = getPatientName(p);
            const pid = getPatientId(p, i);
            return (
              <Card key={pid} className="cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group"
                onClick={() => navigate(`/lumina/clinical-focus?patientId=${pid}`)} data-testid={`patient-card-${pid}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                        <User className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{name}</p>
                        <p className="text-xs text-muted-foreground">{p.gender || p.sex || ''}{(p.gender || p.sex) && ' · '}{p.mrn || p.medical_record_number || ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); openEditPatient(p, i); }}
                        data-testid={`edit-card-${pid}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: pid, name }); }}
                        data-testid={`delete-card-${pid}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {p.date_of_birth || p.dob || '—'}</div>
                    {(p.insurance || p.payer_name) && <Badge variant="outline" className="text-xs">{p.insurance || p.payer_name}</Badge>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Pagination Controls ── */}
      {!loading && patientCount > 0 && (
        <div className="flex items-center justify-between gap-4 py-2" data-testid="pagination-controls">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="page-size-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500" data-testid="page-info">
              Page {page}{totalPages !== null ? ` of ${totalPages}` : ''}{totalCount !== null ? ` · ${totalCount} total` : ''}
            </span>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              disabled={page <= 1} onClick={() => goToPage(page - 1)} data-testid="prev-page-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0"
              disabled={!hasMore} onClick={() => goToPage(page + 1)} data-testid="next-page-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Add Patient Modal ── */}
      <AddPatientModal open={showAddPatient} onOpenChange={(v) => { setShowAddPatient(v); if (!v) setScanPrefill(null); }}
        onSuccess={() => fetchPatients(searchTerm, page, pageSize)} prefill={scanPrefill} />

      {/* ── Upload Patient Data Modal ── */}
      <UploadPatientsModal open={showUpload} onOpenChange={(v) => { setShowUpload(v); if (!v) setPreloadedUploadFile(null); }}
        onSuccess={() => fetchPatients(searchTerm, 1, pageSize)}
        minimizedJobId={minimizedJobId} onMinimize={(jobId) => { setMinimizedJobId(jobId); setShowUpload(false); }}
        onClearMinimized={() => setMinimizedJobId(null)}
        initialFile={preloadedUploadFile} />

      {/* ── Scan ID Modal ── */}
      <ScanIDModal open={showScan} onOpenChange={(v) => { setShowScan(v); if (!v) setPreloadedScanFile(null); }}
        onPrefill={(fields) => { setShowScan(false); setScanPrefill(fields); setShowAddPatient(true); }}
        onSuccess={() => fetchPatients(searchTerm, page, pageSize)}
        initialFile={preloadedScanFile} />

      {/* ── CCDA Import Modal ── */}
      <CCDAImportModal open={showCCDA} onOpenChange={(v) => { setShowCCDA(v); if (!v) setPreloadedCCDAFiles(null); }}
        initialFiles={preloadedCCDAFiles} />

      {/* ── Import Patients Modal (consolidated entry point) ── */}
      <ImportPatientsModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        onStartCSVUpload={(file) => { setPreloadedUploadFile(file); setShowImportModal(false); setShowUpload(true); }}
        onStartCCDAUpload={(files) => { setPreloadedCCDAFiles(files); setShowImportModal(false); setShowCCDA(true); }}
        onStartScan={(file) => { if (file) { setPreloadedScanFile(file); } setShowImportModal(false); setShowScan(true); }}
        onViewHistory={(tab) => {
          setShowImportModal(false);
          if (tab === 'ccda') navigate('/lumina/settings?tab=documents&section=ccda');
          else if (tab === 'scan') navigate('/lumina/settings?tab=documents&section=scan');
          else setShowRollbackHistory(true);
        }}
      />

      {/* ── Single Delete Confirm Dialog ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Delete Patient
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action is irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} data-testid="delete-confirm-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Delete Confirm Dialog ── */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="max-w-sm" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Delete {selectedIds.size} Patient{selectedIds.size > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete {selectedIds.size} selected patient{selectedIds.size > 1 ? 's' : ''}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} data-testid="bulk-delete-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} data-testid="bulk-delete-confirm-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedIds.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Patient Dialog ── */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-patient-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" /> Edit Patient
            </DialogTitle>
            <DialogDescription>Update demographics for <strong>{editTarget?.name}</strong></DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            {[
              { key: 'first_name', label: 'First Name' },
              { key: 'last_name', label: 'Last Name' },
              { key: 'date_of_birth', label: 'Date of Birth', placeholder: 'YYYY-MM-DD' },
              { key: 'gender', label: 'Gender' },
              { key: 'phone', label: 'Phone' },
              { key: 'email', label: 'Email' },
              { key: 'payer_name', label: 'Insurance / Payer' },
              { key: 'member_id', label: 'Member ID' },
            ].map(({ key, label, placeholder }) => (
              <div key={key} className="grid gap-1">
                <Label className="text-xs text-slate-500">{label}</Label>
                <Input
                  value={editForm[key] || ''}
                  placeholder={placeholder || label}
                  onChange={(e) => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                  data-testid={`edit-field-${key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving} data-testid="edit-save-btn">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rollback History Modal ── */}
      <RollbackHistoryModal
        open={showRollbackHistory}
        onOpenChange={setShowRollbackHistory}
        onRollbackSuccess={() => fetchPatients(searchTerm, page, pageSize)}
      />
    </div>
  );
};


export default LuminaPatients;

